import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

export default function PatientHistory({ data, onChange }) {
  const [medicalhx, setMedicalhx] = useState(data.medicalhx || []);
  const [surgicalHistory, setSurgicalHistory] = useState(data.surghx || []);
  const [surgery, setSurgery] = useState('');
  const [surgeryDate, setSurgeryDate] = useState(null);

  useEffect(() => {
    setMedicalhx(data.medicalhx || []);
    setSurgicalHistory(data.surghx || []);
  }, [data.medicalhx, data.surghx]);

  useEffect(() => {
    onChange({
      medicalhx,
      surghx: surgicalHistory,
    });
  }, [medicalhx, surgicalHistory, onChange]);

  const addSurgery = () => {
    if (!surgery.trim() || !surgeryDate) return;

    setSurgicalHistory((prev) => [
      ...prev,
      {
        surgery: surgery.trim(),
        date: dayjs(surgeryDate).toISOString(),
      },
    ]);

    setSurgery('');
    setSurgeryDate(null);
  };

  const removeSurgery = (indexToRemove) => {
    setSurgicalHistory((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #60A5FA',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: '#dbeafe',
            borderBottom: '1px solid #60A5FA',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MedicalInformationOutlinedIcon />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Patient History</Typography>
            <Typography variant="body2" color="text.secondary">
              Record the patient’s past medical and surgical history.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
                Past Medical History
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Type a diagnosis or condition and press Enter to add it.
              </Typography>

              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={medicalhx}
                onChange={(event, newValue) => setMedicalhx(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add medical history"
                    placeholder="e.g. Asthma, epilepsy, prematurity"
                    fullWidth
                  />
                )}
              />

              {medicalhx.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  No past medical history added yet.
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
                Surgical History
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Add previous operations or procedures with a date.
              </Typography>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  sx={{ mb: 2 }}
                >
                  <TextField
                    fullWidth
                    label="Procedure"
                    value={surgery}
                    onChange={(e) => setSurgery(e.target.value)}
                    placeholder="e.g. Appendicectomy"
                  />

                  <DatePicker
                    label="Date"
                    value={surgeryDate ? dayjs(surgeryDate) : null}
                    onChange={setSurgeryDate}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { minWidth: { md: 180 } },
                      },
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={addSurgery}
                    startIcon={<AddIcon />}
                    sx={{ textTransform: 'none', minWidth: 140 }}
                  >
                    Add surgery
                  </Button>
                </Stack>
              </LocalizationProvider>

              {surgicalHistory.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No surgical history added yet.
                </Alert>
              ) : (
                <Stack spacing={1.25}>
                  {surgicalHistory.map((item, index) => (
                    <Paper
                      key={`${item.surgery}-${item.date}-${index}`}
                      elevation={0}
                      sx={{
                        border: '1px solid #d1d5db',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                            {item.surgery}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(item.date).format('DD/MM/YYYY')}
                          </Typography>
                        </Box>

                        <IconButton
                          edge="end"
                          aria-label="delete surgery"
                          onClick={() => removeSurgery(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}